import React from 'react';
import { media } from '@minecraft.themes';
import Icon from '@minecraft.icon';
import styled, { withTheme } from 'styled-components';
import MeatballMenu from '@minecraft.meatball-menu';
import DropdownItem from '@minecraft.dropdown-item';
import { SCREEN_NAMES } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';
import { PrimaryButton } from '@minecraft.atoms';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { useProfileViewContext } from '../ProfileViewContext';
import { UserInfoContainer, InfoHeading } from '../styles/ProfileView';
import { MediaUploader } from '../../MediaUploader';

const sectionName = 'resume';

const UserInfoBtnContainer = styled.div`
  background: ${(props): string => props.theme.colors.accent.greyBlue2};
  padding: 1rem 1rem 1rem 3rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin: 0 1rem;
  ${media.sm`
    margin: 0;
    justify-content: flex-start;
  `}
  input {
    position: absolute;
    left: 0;
    width: 100%;
    opacity: 0;
  }
`;

const UserInfoSection = styled.div`
  background: ${(props): string => props.theme.colors.primary.white};
  position: relative;
  padding: 1rem;
  ${media.sm`
    padding: 1rem 3rem;
  `}
`;

const UserInfoHeading = styled.h3`
  font-size: 1.7rem;
  line-height: 2rem;
`;

const StyledMeatballMenu = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`;

const StyledResumeLink = styled.a`
  display: block;
  width: 100%;
  max-width: 865px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledTooltip = styled(UncontrolledTooltip)`
  .tooltip-inner {
    max-width: 100%;
  }
`;

const Resume = ({ theme }): JSX.Element => {
  const { t } = useTranslation();
  const { isPreview, updater, fetcher } = useProfileViewContext();
  const { profile } = fetcher;
  const { setResume, removeResume } = updater;

  return (
    <UserInfoContainer isPreview={isPreview} id="div-resume" data-testid="profile-resume-container">
      <MediaUploader
        data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-upload`}
        mediaType={MediaTypeCodeEnum.Document}
        renderChildren={({ toggleModal }) => {
          return profile.resume && profile.resume.media ? (
            <UserInfoSection>
              <UserInfoHeading>{t('common:profile.nav.resume')}</UserInfoHeading>
              <StyledResumeLink
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-name`}
                rel="noopener noreferrer"
                href={profile.resume.media.url}
                target="_blank"
                id="profile-resume"
              >
                <Icon
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-icon`}
                  name="spicerack-resume"
                  color={theme.colors.primary.primary}
                />{' '}
                {profile.resume.media.name}
              </StyledResumeLink>
              {profile.resume.media.name.length > 100 && (
                <StyledTooltip
                  data-qa-id="resume-name-tooltip"
                  placement="bottom"
                  autohide={false}
                  target="profile-resume"
                >
                  {profile.resume.media.name}
                </StyledTooltip>
              )}
              {!isPreview && (
                <StyledMeatballMenu>
                  <MeatballMenu qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-options`} direction="right">
                    <DropdownItem
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-delete`}
                      onClick={() => toggleModal()}
                    >
                      {t('common:profile.resumeUpdateNew')}
                    </DropdownItem>
                    <DropdownItem
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-delete`}
                      onClick={() => removeResume()}
                    >
                      {t('common:button.delete')}
                    </DropdownItem>
                  </MeatballMenu>
                </StyledMeatballMenu>
              )}
            </UserInfoSection>
          ) : (
            !isPreview && (
              <>
                <InfoHeading>{t('common:profile.nav.resume')}</InfoHeading>
                <UserInfoBtnContainer>
                  <PrimaryButton
                    variant="secondary"
                    label={t('common:profile.addYourResume')}
                    onClick={() => toggleModal()}
                  />
                </UserInfoBtnContainer>
              </>
            )
          );
        }}
        onCloseUploader={async (uploadResult) => {
          if (!uploadResult?.length) return;
          await setResume({ mediaId: uploadResult[0].mediaId });
        }}
        maxFiles={1}
      />
    </UserInfoContainer>
  );
};

export default withTheme(Resume);
